import './Tabs.less';
import { throttle } from 'lodash';

import React, { FC, useCallback, useRef, useState, useEffect } from 'react';
import clsx from 'clsx';
import { trackEvent } from '../../../analytics/gtm';
import { ArrowSvg } from '../../Arrows/Arrows';
import { SvgIcon } from '../../../icons/SvgIcon';

const Tabs: FC<{
    otherClass?: string;
    leftArrowVisible?;
    rightArrowVisible?;
    setArrowVisible?;
    wrapRef?: any;
    isLiveEvent?: boolean;
    repaint?: any;
    transparent?: boolean;
    dark?: boolean;
    defaultColor?: boolean;
    ng?: boolean;
}> = ({
    children,
    otherClass,
    leftArrowVisible,
    rightArrowVisible,
    setArrowVisible,
    wrapRef,
    isLiveEvent,
    repaint,
    transparent = false,
    dark = false,
    defaultColor = true,
    ng
}) => {
    const leftArrowBgClass = dark ? 'tabs-arrow-dark' : transparent ? 'tabs-arrow-light' : 'tabs-arrow';
    const rightArrowBgClass = dark
        ? 'tabs-arrow-dark-right'
        : transparent
        ? 'tabs-arrow-light-right'
        : 'tabs-arrow-right';
    useEffect(() => {
        if (repaint && children) {
            repaint();
        }
    }, [children, repaint]);
    return (
        <div
            className={clsx('tabs flex p-rel', ng ? 'c-black-50' : 'c-default', otherClass, {
                'tabs-bgr': !transparent && !ng,
                'tabs-bgr-ng': ng
            })}
            data-game-state={isLiveEvent ? 'LIVE' : 'PREMATCH'}
        >
            {leftArrowVisible && (
                <button
                    className={clsx('flex items-center px-10 p-abs full-height', leftArrowBgClass)}
                    onClick={() => setArrowVisible(TABS_SCROLL_DIRECTION_LEFT)}
                >
                    <ArrowSvg sizeX="16" sizeY="16" otherClassName="arrow-left arrow-orange" />
                </button>
            )}
            <div
                className={clsx('tabs-scrollable  flex full-width', {
                    'tabs-scrollable-defaut': defaultColor,
                    'tabs-gap': ng || defaultColor
                })}
                ref={children ? wrapRef : null}
            >
                {children}
            </div>
            {rightArrowVisible && (
                <button
                    className={clsx('flex items-center px-10 p-abs full-height', rightArrowBgClass)}
                    onClick={() => setArrowVisible(TABS_SCROLL_DIRECTION_RIGHT)}
                >
                    <ArrowSvg sizeX="16" sizeY="16" otherClassName="arrow-right arrow-orange" />
                </button>
            )}
        </div>
    );
};

const HEADER_OFFSET = 80;
export const TABS_SCROLL_DIRECTION_LEFT = 'LEFT';
export const TABS_SCROLL_DIRECTION_RIGHT = 'RIGHT';

//je to samanstvo, black magic, lost magic, forbidden magic - trosku ina ako pred tym, ale stale rovnako na...
export function useTabs(initialActive: string | number) {
    const [leftArrow, setLeftArrow] = useState(false);
    const [rightArrow, setRightArrow] = useState(false);
    const tabsWrapRef = useRef(null);
    //tab chcange controller
    const scrollElementRef = useCallback(node => {
        if (node) {
            scrollToElementPosition(node);
        }
    }, []);

    const [active, setActive] = useState(null);

    useEffect(() => setActive(initialActive), [initialActive]);

    //tab click controller
    const onTabClick = useCallback((event, tab) => {
        setActive(tab);
    }, []);

    //hanbim sa za toto riesenie, ale nenasiel som lepsi sposob, ako prinutit toto fungovat na portaly,
    //kde ked sa raz taby mountnu tak sa nererenderuju resp. nererenderuje sa wrap okolo nich,
    //cize aj ked sa zmeni content, nie som schopny detekovat zmenu sirky, takto "si vykonam kontrolu a zmenu ked to ptorebujem"
    function triggerRepaint() {
        if (tabsWrapRef.current) {
            if (tabsWrapRef.current.scrollLeft < 20) {
                setLeftArrow(false);
            } else {
                setLeftArrow(true);
            }
            if (
                tabsWrapRef.current.scrollWidth - tabsWrapRef.current.scrollLeft <
                tabsWrapRef.current.offsetWidth + 20
            ) {
                setRightArrow(false);
            } else {
                setRightArrow(true);
            }
        }
    }

    //trigger only on initial mount
    const tabsWrapFakeRef = useCallback(
        node => {
            //arrow visibitily controller
            const scrollHandler = throttle(() => {
                if (node.scrollLeft < 20) {
                    setLeftArrow(false);
                } else {
                    setLeftArrow(true);
                }
                if (node.scrollWidth - node.scrollLeft < node.offsetWidth + 20) {
                    setRightArrow(false);
                } else {
                    setRightArrow(true);
                }
            }, 100);
            if (tabsWrapRef.current) {
                tabsWrapRef.current.removeEventListener('scroll', scrollHandler, false);
            }
            if (node) {
                tabsWrapRef.current = node;
                tabsWrapRef.current.addEventListener('scroll', scrollHandler, false);
            }
        },
        [tabsWrapRef]
    );

    function scrollToElementPosition(element) {
        if (
            element &&
            (element.offsetLeft + element.offsetWidth + HEADER_OFFSET >
                element.parentElement.scrollLeft + element.parentElement.clientWidth ||
                element.parentElement.scrollLeft > element.offsetLeft - HEADER_OFFSET ||
                element.parentElement.scrollWidth - element.offsetLeft + HEADER_OFFSET <
                    element.parentElement.clientWidth)
        ) {
            if (element.parentElement.scrollLeft > element.offsetLeft - HEADER_OFFSET) {
                element.parentElement.scrollLeft =
                    element.offsetLeft - element.parentElement.clientWidth + element.offsetWidth + HEADER_OFFSET;
            } else {
                element.parentElement.scrollLeft = element.offsetLeft - HEADER_OFFSET;
            }
        }
    }

    function setArrowVisible(direction) {
        const element = tabsWrapRef.current;
        if (element) {
            if (direction === 'RIGHT') {
                element.scrollLeft = element.scrollLeft + element.offsetWidth;
            } else {
                element.scrollLeft = element.scrollLeft - element.offsetWidth;
            }
        }
    }

    return [
        onTabClick,
        setArrowVisible,
        tabsWrapFakeRef,
        scrollElementRef,
        active,
        leftArrow,
        rightArrow,
        triggerRepaint
    ];
}

export function TabButton({
    label,
    imgId,
    active,
    onTabChange,
    name,
    scrollRef,
    className,
    'data-atid': dataAtid = '',
    badgeCount = null,
    trackData,
    disabled = false,
    ng
}: {
    label: any;
    imgId?: string;
    active: any;
    onTabChange;
    name: string;
    scrollRef?: any;
    className?: string;
    'data-atid'?: string;
    badgeCount?: number;
    trackData?: any;
    disabled?: boolean;
    ng?: boolean;
}) {
    const isActive = active === name;
    const ngActive = ng && isActive;
    function onTabClick(e) {
        if (trackData) {
            trackEvent(trackData.category, trackData.action, trackData.label);
        }
        if (e) {
            e.preventDefault();
            e.stopPropagation();
        }
        onTabChange(e, name);
    }

    return (
        <button
            className={clsx(
                'tab-button px-30',
                {
                    active: isActive,
                    disabled: disabled,
                    'flex-center italic fs-16 bg-white-box': ng,
                    'c-black-100': ngActive
                },
                className
            )}
            ref={isActive ? scrollRef : null}
            onClick={onTabClick}
            disabled={isActive || disabled}
            data-atid={dataAtid}
            data-tab-name={name}
        >
            {imgId && (
                <SvgIcon
                    imgId={imgId}
                    sizeX="16"
                    sizeY="16"
                    className={`mr-10 ${isActive ? 'c-orange' : 'c-black-25'}`}
                />
            )}
            {badgeCount || ngActive ? (
                <span
                    className={clsx('p-rel', { 'tabs-badge': badgeCount, 'tabs-bold': ngActive })}
                    data-badge={badgeCount}
                    data-name={label}
                >
                    {label}
                </span>
            ) : (
                label
            )}
        </button>
    );
}

export default Tabs;
